/**
 * Out-of-schedule MMR: Travel and Outbreak (KIN-1875, NIIS Phase 2 §3).
 *
 * A child needing an MMR before twelve months — leaving the country, or caught in a
 * measles outbreak — had that dose recorded in the PCI dose-1 slot, because it was the
 * only slot the form offered, and the system then refused the real PCI dose when it came
 * due. These are the third place to put that dose.
 */

export const VACCINATION_PROGRAMME = {
  primaryChildhood: "PRIMARY_CHILDHOOD",
  travel: "X0071-3",
  outbreak: "X0071-2",
};

export const VACCINATION_PROGRAMME_OPTIONS = [
  { value: VACCINATION_PROGRAMME.primaryChildhood, label: "Primary Childhood Immunisation" },
  { value: VACCINATION_PROGRAMME.travel, label: "Travel" },
  { value: VACCINATION_PROGRAMME.outbreak, label: "Outbreak" },
];

/** The programme choice applies to MMR and nothing else — the backend HARD_STOPs the rest. */
export const isOutOfScheduleProgrammeOffered = (vaccineType) =>
  String(vaccineType || "").toUpperCase().includes("MMR");

/**
 * What to send as `vaccinationProgrammeCode`.
 *
 * Primary Childhood sends nothing at all. Null means "derive it from the vaccine", which
 * is what every existing record carries and what every non-MMR record always will; a
 * `X0071-1` written explicitly would work but would split one meaning across two values.
 */
export const toProgrammeCode = (programme) =>
  programme && programme !== VACCINATION_PROGRAMME.primaryChildhood
    ? programme
    : undefined;

export const isOutbreakProgramme = (programme) =>
  programme === VACCINATION_PROGRAMME.outbreak;

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The campaigns that cover the date the dose was given.
 *
 * Judged on the administration date and never on today's, because a dose given during an
 * outbreak has to stay recordable after the campaign ends — retrospective entry is the
 * ordinary case here, not the exception. The backend enforces the same window on save, so
 * filtering on anything else would show a picker whose choices are refused.
 *
 * A bound may be null, meaning unbounded at that end.
 */
export const outbreaksCoveringDate = (outbreaks, vaccinationDate) => {
  const given = toDate(vaccinationDate);

  if (!given) return [];

  return (outbreaks || []).filter(({ activeFrom, activeTo }) => {
    const from = toDate(activeFrom);
    const to = toDate(activeTo);

    if (from && given < from) return false;
    if (to && given > to) return false;

    return true;
  });
};

export const toOutbreakOptions = (outbreaks) =>
  (outbreaks || []).map(({ code, description }) => ({
    value: code,
    label: description || code,
  }));
